import { ItemGroupI, ItemI } from "../shared/item";
import { storeRoomInterface } from "../store/useStoreRoom";
import { ShelvingStoreInterface } from "../store/useShelvingStore";
import { Sides, BeamInterface } from "../store/types/store";
import { DecorItemI, OriginDecorI } from "../shared";
import { normalizeWorkbenchToolboxDecor } from "./workbenchToolbox";

/**
 * Создает карту bitrixId -> FILE_3D для быстрого поиска актуальных model ссылок
 */
function createModelUrlMap(itemsGroup: ItemGroupI[], decors: Record<string, OriginDecorI>): Map<string, string> {
    const modelUrlMap = new Map<string, string>();

    itemsGroup.forEach((group: ItemGroupI) => {
        if (!group.ELEMENTS) return;

        group.ELEMENTS.forEach((item: ItemI) => {
            if (item.PROPERTIES.FILE_3D) {
                modelUrlMap.set(item.BITRIX_OFFER_ID, item.PROPERTIES.FILE_3D);
                if (item.BITRIX_OFFER_ID_ALT) {
                    modelUrlMap.set(item.BITRIX_OFFER_ID_ALT, item.PROPERTIES.FILE_3D);
                }
                if (item.BITRIX_OFFERS_IDS) {
                    item.BITRIX_OFFERS_IDS.forEach((id) => {
                        if (id) {
                            modelUrlMap.set(id, item.PROPERTIES.FILE_3D);
                        }
                    });
                }
            }
        });
    });

    Object.entries(decors).map(([id, value]) => {
        if (value?.PROPERTIES?.FILE_3D) {
            modelUrlMap.set(id, value.PROPERTIES.FILE_3D);
            if (value?.BITRIX_OFFERS_IDS) {
                value.BITRIX_OFFERS_IDS.forEach((id) => {
                    if (id) {
                        modelUrlMap.set(id, value.PROPERTIES.FILE_3D);
                    }
                });
            }
        }
    });

    return modelUrlMap;
}

/**
 * Создает карту modelId -> FILE_3D для быстрого поиска актуальных model ссылок по ID модели
 */
function createModelIdUrlMap(itemsGroup: ItemGroupI[], decors: Record<string, OriginDecorI>): Map<string, string> {
    const modelIdUrlMap = new Map<string, string>();

    itemsGroup.forEach((group: ItemGroupI) => {
        if (!group.ELEMENTS) return;

        group.ELEMENTS.forEach((item: ItemI) => {
            if (item.PROPERTIES.FILE_3D && item.ID) {
                modelIdUrlMap.set(item.ID, item.PROPERTIES.FILE_3D);
            }
        });
    });

    Object.entries(decors).map(([id, value]) => {
        if (value?.PROPERTIES?.FILE_3D) modelIdUrlMap.set(id, value.PROPERTIES.FILE_3D);
    });

    return modelIdUrlMap;
}

/**
 * Обновляет model ссылку для объекта с bitrixId если найдена актуальная ссылка
 */
function updateObjectModel<T extends { model?: string; config?: { url?: string }; bitrixId?: string }>(
    obj: T,
    modelUrlMap: Map<string, string>
): T {
    if (!obj.bitrixId || (!obj.model && !obj?.config?.url)) return obj;

    const updatedUrl = modelUrlMap.get(`${obj.bitrixId}`) || modelUrlMap.get(obj.bitrixId);
    if (obj?.config) console.log(updatedUrl, obj, modelUrlMap, obj.model, obj?.config?.url);
    if (updatedUrl && updatedUrl !== obj.model && updatedUrl !== obj?.config?.url) {
        console.log(
            `Model URL updated for bitrixId ${obj.bitrixId}: ${obj?.model || obj?.config?.url} -> ${updatedUrl}`
        );
        return {
            ...obj,
            ...(obj?.config?.url ? { config: { ...obj.config, url: updatedUrl } } : { model: updatedUrl }),
        };
    }

    return obj;
}

/**
 * Обрабатывает специальные случаи beams: находит подходящие элементы с "Горизонтальная" в названии
 * и сопоставляет их по ширине
 */
function updateBeamModel(beam: BeamInterface, itemsGroup: ItemGroupI[]): typeof beam {
    if (!beam || !beam.width || typeof beam.width !== "number") {
        return beam;
    }

    // Ищем элементы с "Горизонтальная" в названии
    const horizontalElements: ItemI[] = [];
    itemsGroup.forEach((group: ItemGroupI) => {
        if (!group.ELEMENTS) return;

        group.ELEMENTS.forEach((item: ItemI) => {
            if (item.NAME && item.NAME.includes("Горизонтальная")) {
                horizontalElements.push(item);
            }
        });
    });

    // Для каждого найденного элемента сравниваем LENGTH с шириной beam
    for (const element of horizontalElements) {
        if (!element.PROPERTIES.LENGTH) continue;

        const elementLength = Number(element.PROPERTIES.LENGTH);
        if (isNaN(elementLength)) continue;

        // Преобразуем LENGTH согласно таблице соответствий
        let mappedLength: number;
        if (elementLength === 512) {
            mappedLength = 480;
        } else if (elementLength === 632) {
            mappedLength = 600;
        } else if (elementLength === 832) {
            mappedLength = 800;
        } else if (elementLength === 1232) {
            mappedLength = 1200;
        } else {
            continue; // Пропускаем элементы с неизвестными длинами
        }

        // Сравниваем с шириной beam
        if (mappedLength === beam.width) {
            const updatedBeam = { ...beam };

            // Записываем найденный ID в beam
            if (element.ID) {
                updatedBeam.modelId = element.ID;
            }

            // Записываем FILE_3D если есть
            if (element.PROPERTIES.FILE_3D) {
                updatedBeam.model = element.PROPERTIES.FILE_3D;
            }

            console.log(
                `Beam model updated: width=${beam.width} matched with element ID=${element.ID}, LENGTH=${elementLength} -> ${mappedLength}`
            );
            return updatedBeam;
        }
    }

    return beam;
}

/**
 * Рекурсивно обновляет все model ссылки в объекте или массиве
 */
function updateModelUrlsRecursively(
    obj: any,
    modelUrlMap: Map<string, string>,
    modelIdUrlMap: Map<string, string>
): any {
    if (!obj || typeof obj !== "object") {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map((item) => updateModelUrlsRecursively(item, modelUrlMap, modelIdUrlMap));
    }

    let updatedObj = { ...obj };

    // Обработка для обратной совместимости:
    // 1. Если есть modelId, но нет model - находим URL по modelId
    // 2. Если есть model и bitrixId, обновляем его через bitrixId
    if ("modelId" in updatedObj && updatedObj.modelId && !updatedObj.model) {
        // Для новых проектов: modelId содержит ID модели, получаем URL из карты
        const modelUrl = modelIdUrlMap.get(updatedObj.modelId);
        if (modelUrl) {
            updatedObj.model = modelUrl;
        }
    } else if (("model" in updatedObj || "config" in updatedObj) && "bitrixId" in updatedObj) {
        // Для старых проектов: обновляем model через bitrixId
        updatedObj = { ...updatedObj, ...updateObjectModel(updatedObj, modelUrlMap) };
    }

    if (
        "bitrixId" in updatedObj &&
        "width" in updatedObj &&
        "height" in updatedObj &&
        "depth" in updatedObj &&
        "position" in updatedObj
    ) {
        updatedObj = normalizeWorkbenchToolboxDecor(updatedObj as DecorItemI);
    }

    // Рекурсивно обрабатываем все свойства объекта
    for (const key in updatedObj) {
        if (updatedObj.hasOwnProperty(key)) {
            updatedObj[key] = updateModelUrlsRecursively(updatedObj[key], modelUrlMap, modelIdUrlMap);
        }
    }

    return updatedObj;
}

/**
 * Универсальная функция для обновления всех model ссылок в состоянии проекта
 * Обрабатывает как roomState, так и shelvingState
 */
export function updateAllModelUrls(
    project: {
        roomState?: DeepPartial<storeRoomInterface> | undefined;
        shelvingState?: DeepPartial<ShelvingStoreInterface> | undefined;
    },
    itemsGroup: ItemGroupI[],
    decors: Record<string, OriginDecorI>
): typeof project {
    if (!itemsGroup || itemsGroup.length === 0) {
        return project;
    }

    const modelUrlMap = createModelUrlMap(itemsGroup, decors);
    const modelIdUrlMap = createModelIdUrlMap(itemsGroup, decors);
    const updatedProject = { ...project };

    // Обновляем roomState
    if (updatedProject.roomState) {
        updatedProject.roomState = updateModelUrlsRecursively(updatedProject.roomState, modelUrlMap, modelIdUrlMap);
    }
    if (!updatedProject.shelvingState) {
        return updatedProject;
    }

    // Обновляем shelvingState
    if (updatedProject.shelvingState) {
        updatedProject.shelvingState = updateModelUrlsRecursively(
            updatedProject.shelvingState,
            modelUrlMap,
            modelIdUrlMap
        );
        const shelvingState = updatedProject.shelvingState;
        if (!shelvingState || !shelvingState.shelving) {
            return updatedProject;
        }
        // Отдельная обработка beams для всех 4 сторон
        if (updatedProject.shelvingState?.shelving) {
            const sides = ["front", "left", "right", "back"] as Sides[];
            sides.forEach((side) => {
                if (shelvingState?.shelving?.[side] && shelvingState?.shelving?.[side]?.beams) {
                    shelvingState.shelving[side]!.beams =
                        updatedProject.shelvingState?.shelving?.[side]?.beams?.map((beam) =>
                            updateBeamModel(beam as BeamInterface, itemsGroup)
                        ) ?? [];
                }
            });
        }

        // Отдельная обработка колес в parts
        if (updatedProject.shelvingState?.shelving) {
            const sides = ["front", "left", "right", "back"] as Sides[];
            sides.forEach((side) => {
                if (shelvingState?.shelving?.[side]?.sections) {
                    shelvingState.shelving[side]!.sections = shelvingState.shelving[side]?.sections?.map((section) => ({
                        ...section,
                        items: section?.items?.map((item) => ({
                            ...item,
                            parts: item?.parts?.map((part) => {
                                if (part?.name?.toLowerCase().includes("колесо")) {
                                    for (const group of itemsGroup) {
                                        if (!group.ELEMENTS) continue;

                                        for (const element of group.ELEMENTS) {
                                            if (element.NAME && element.NAME.toLowerCase().includes("колесо")) {
                                                return {
                                                    ...part,
                                                    modelId: element.ID || part.modelId,
                                                    model: element.PROPERTIES.FILE_3D || part.model,
                                                };
                                            }
                                        }
                                    }
                                }
                                return part;
                            }),
                        })),
                    }));
                }
            });
        }
    }

    return updatedProject;
}
